// Serialise / restore a whole selection for "Save & share link".
//
// Stored: the sectors (NIS9, postcode, municipality, households, centroid) WITHOUT geometry, the
// directly-counted postcodes, the segmentation (pickers + dividual codes), the threshold and the
// e-mail checkbox, plus the bbox of the sectors. A few KB even for a province. On restore the
// sector polygons are fetched again from the WFS by that bbox and matched on NIS9; a sector that
// is no longer in the layer keeps its row without a polygon.

import { tiersToIncomeRange, INCOME_MIN, INCOME_MAX } from './dividualMapping.js';
import { t } from './i18n.js';

export const FORMAT = 1;

export function serializeSelection(state) {
  const sectors = [];
  let mnLng = Infinity, mnLat = Infinity, mxLng = -Infinity, mxLat = -Infinity;
  state.sectors.forEach((s) => {
    sectors.push({
      n: s.nis9, pc: s.postcode || null, m: s.municipality && s.municipality !== '…' ? s.municipality : '',
      hh: s.totalHH || 0, lng: s.lng ?? null, lat: s.lat ?? null
    });
    if (s.lng != null && s.lat != null) {
      if (s.lng < mnLng) mnLng = s.lng; if (s.lat < mnLat) mnLat = s.lat;
      if (s.lng > mxLng) mxLng = s.lng; if (s.lat > mxLat) mxLat = s.lat;
    }
  });
  return {
    format: FORMAT,
    sectors,
    bbox: isFinite(mnLng) ? [mnLng, mnLat, mxLng, mxLat] : null,
    pcUnits: [...state.pcUnits],
    dividuals: [...state.dividuals],
    friendly: {
      familySizes: [...state.friendly.familySizes],
      houseTypes: [...state.friendly.houseTypes],
      ageRange: [...state.friendly.ageRange],
      incomeRange: [...state.friendly.incomeRange]
    },
    minTargetPct: state.minTargetPct,
    includeSegmentedEmail: !!state.includeSegmentedEmail,
    waveLang: state.waveLang || null
  };
}

// ctx: the handful of main.js internals the restore needs (DOM helper, state, renderers, map).
export async function restoreSelection(sel, ctx) {
  const { $, state, emit, refreshUI, scheduleRecount, updateCorresponding, renderDividualsList,
    getLastMatched, setLastPickerAny, ageRangeCtl, incomeRangeCtl, catalogueByCode, dividualsReady,
    fetchSectorsInBBox, fetchSectorsByCodes, fitToSelection, setStatus, mapLoad, nis5NameCache } = ctx;
  if (!sel || typeof sel !== 'object') throw new Error('empty selection');

  // ---- area ----
  state.sectors.clear();
  state.pcUnits.clear();
  state.counts.clear();
  (sel.pcUnits || []).forEach((pc) => state.pcUnits.add(String(pc)));

  const saved = Array.isArray(sel.sectors) ? sel.sectors : [];
  let geom = new Map();
  if (saved.length && Array.isArray(sel.bbox)) {
    setStatus(t('st.loadingZones', { done: 0, total: saved.length }));
    mapLoad('restore', t('st.loadingZones', { done: 0, total: saved.length }));
    const pad = 0.01;
    const [a, b, c, d] = sel.bbox;
    try {
      const infos = await fetchSectorsInBBox([a - pad, b - pad, c + pad, d + pad], 12000);
      infos.forEach((i) => geom.set(i.nis9, i));
    } catch { /* fall through: fetched by code below */ }
  }
  // Whatever the bbox fetch missed (a bbox too big for one WFS answer, a server cap, a timeout):
  // fetch by code, one request per municipality. Without this those sectors had no polygon, so
  // they were invisible when zoomed out and "suddenly selected" when zoomed in, where the WFS
  // layer itself styles them.
  const missing = saved.map((s) => s.n).filter((n) => !geom.has(n) && !String(n).startsWith('PC'));
  if (missing.length && fetchSectorsByCodes) {
    try {
      const found = await fetchSectorsByCodes(missing, (done, total) => mapLoad('restore', t('st.loadingSectorsProgress', { done, total })));
      found.forEach((info, code) => geom.set(code, info));
    } catch { /* offline: rows without polygons */ }
  }
  saved.forEach((s) => {
    const g = geom.get(s.n);
    state.sectors.set(s.n, {
      nis9: s.n, postcode: s.pc || null, municipality: s.m || '',
      totalHH: g ? g.totalHH : (s.hh || 0),
      feature: g ? g.feature : null,
      lng: g ? g.lng : s.lng, lat: g ? g.lat : s.lat
    });
    if (s.m && /^\d{5}/.test(s.n)) nis5NameCache.set(s.n.slice(0, 5), s.m);
  });

  // ---- segmentation pickers ----
  const f = sel.friendly || {};
  const setGrid = (gridId, setKey, values) => {
    const set = state.friendly[setKey];
    set.clear();
    (values || []).forEach((v) => set.add(v));
    $(gridId).querySelectorAll('.picker-tile').forEach((el) => {
      el.classList.toggle('selected', set.has(el.dataset.value));
    });
  };
  setGrid('family-size-grid', 'familySizes', f.familySizes);
  setGrid('house-type-grid', 'houseTypes', f.houseTypes);
  // Income: index range since 2026-10-15; older links carry tiers 0/1/2 and are mapped over.
  let inc = Array.isArray(f.incomeRange) && f.incomeRange.length === 2 ? f.incomeRange.map(Number) : null;
  if (!inc && Array.isArray(f.incomeLevels) && f.incomeLevels.length) inc = tiersToIncomeRange(f.incomeLevels);
  if (!inc) inc = [INCOME_MIN, INCOME_MAX];
  inc = [Math.max(INCOME_MIN, inc[0]), Math.min(INCOME_MAX, inc[1])];
  state.friendly.incomeRange = inc;
  if (incomeRangeCtl) incomeRangeCtl.set(inc[0], inc[1]);
  const age = Array.isArray(f.ageRange) && f.ageRange.length === 2 ? f.ageRange : [18, 90];
  state.friendly.ageRange = [Number(age[0]), Number(age[1])];
  if (ageRangeCtl) ageRangeCtl.set(state.friendly.ageRange[0], state.friendly.ageRange[1]);

  // ---- threshold + e-mail ----
  if (Number.isFinite(Number(sel.minTargetPct))) {
    state.minTargetPct = Math.max(0, Math.min(100, Number(sel.minTargetPct)));
    $('min-pct-slider').value = String(state.minTargetPct);
    $('min-pct-value').textContent = String(state.minTargetPct);
  }
  state.includeSegmentedEmail = !!sel.includeSegmentedEmail;
  $('include-email').checked = state.includeSegmentedEmail;
  state.waveLang = ['nl', 'fr'].includes(sel.waveLang) ? sel.waveLang : null;
  document.querySelectorAll('.wave-lang button').forEach((b) => b.classList.toggle('selected', (b.dataset.waveLang || null) === state.waveLang));

  // ---- dividuals: wait for the catalogue, derive from pickers, then apply the saved codes
  //      exactly (the user may have (un)checked some by hand) ----
  try { await dividualsReady; } catch { /* list shows its own error */ }
  updateCorresponding();
  const codes = (sel.dividuals || []).filter((c) => catalogueByCode.has(c));
  state.dividuals = new Set(codes);
  setLastPickerAny(getLastMatched() !== null);
  renderDividualsList(getLastMatched());

  emit('sectors-changed');
  refreshUI();
  scheduleRecount();
  if (state.sectors.size) fitToSelection(60);
  setStatus('');
}
